import type { PrayerNote } from '../domain.ts';

/**
 * E1 · E2 캘린더.
 *
 * 날짜 밑 점이 그 날 기도 노트의 존재와 성격을 나타냅니다.
 * 오늘은 '채움', 선택 날짜는 '테두리' — 둘이 동시에 보여도 구분되어야 합니다.
 *
 * Everyday 노트는 점을 찍지 않습니다. 매일이면 달력에 표시할 의미가 없습니다 (기획서 3.4).
 */

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];

function iso(y: number, m: number, d: number): string {
  return `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

/** 점 색은 그 날 노트 중 가장 강한 신호를 따릅니다: 긴급 > 응답완료 > 일반 */
function dotKind(notes: PrayerNote[]): 'urgent' | 'answered' | 'normal' {
  if (notes.some((n) => n.priority === 'URGENT')) return 'urgent';
  if (notes.some((n) => n.status === 'ANSWERED')) return 'answered';
  return 'normal';
}

interface Props {
  year: number;
  month: number; // 0-based
  notes: PrayerNote[];
  today: string;
  selected: string | null;
  onSelect: (date: string) => void;
}

export function Calendar({ year, month, notes, today, selected, onSelect }: Props) {
  const first = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const leading = first.getDay();

  // 날짜별 노트 묶음. Everyday 는 제외합니다.
  const byDate = new Map<string, PrayerNote[]>();
  for (const n of notes) {
    if (n.plan.type !== 'DATE') continue;
    const list = byDate.get(n.plan.date) ?? [];
    list.push(n);
    byDate.set(n.plan.date, list);
  }

  const cells: { date: string | null; day: number; dim: boolean }[] = [];
  for (let i = 0; i < leading; i++) cells.push({ date: null, day: 0, dim: true });
  for (let d = 1; d <= daysInMonth; d++) cells.push({ date: iso(year, month, d), day: d, dim: false });

  return (
    <div className="cal" role="grid" aria-label={`${year}년 ${month + 1}월`}>
      {WEEKDAYS.map((w, i) => (
        <span key={w} className={i === 0 ? 'cal__dow cal__dow--sun' : 'cal__dow'}>
          {w}
        </span>
      ))}

      {cells.map((cell, i) => {
        if (!cell.date) return <span key={`pad-${i}`} className="cal__day cal__day--pad" />;
        const dayNotes = byDate.get(cell.date) ?? [];
        const isToday = cell.date === today;
        const isSel = cell.date === selected;
        const sunday = i % 7 === 0;

        const cls = [
          'cal__day',
          sunday ? 'cal__day--sun' : '',
          isToday ? 'cal__day--today' : '',
          isSel && !isToday ? 'cal__day--sel' : '',
        ]
          .filter(Boolean)
          .join(' ');

        return (
          <button
            key={cell.date}
            type="button"
            className={cls}
            aria-pressed={isSel}
            aria-label={`${cell.day}일${dayNotes.length ? `, 기도 노트 ${dayNotes.length}` : ''}`}
            onClick={() => onSelect(cell.date!)}
          >
            <span className="cal__num">{cell.day}</span>
            <span className="cal__dots">
              {dayNotes.length > 0 && (
                <i className={`cal__dot cal__dot--${dotKind(dayNotes)}`} />
              )}
              {dayNotes.length > 1 && <i className="cal__dot cal__dot--normal" />}
            </span>
          </button>
        );
      })}
    </div>
  );
}
